/**
 * Subscription management page with metrics and actions.
 */

import { useEffect, useState } from 'react'
import { api, ApiRequestError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { DataTable, Column } from '@/components/DataTable'
import { formatCurrency, formatDate, formatNumber, formatPercent, getStatusConfig } from '@/lib/formatters'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/useToast'

interface Subscription {
  id: number
  subscription_no: string
  customer_email: string
  product_name: string
  status: string
  billing_cycle: string
  amount: number
  currency: string
  current_period_start: string
  current_period_end: string
  next_billing_date: string | null
  created_at: string
}

interface SubscriptionMetrics {
  active_count: number
  mrr: number
  churn_rate: number
  avg_lifetime_value: number
}

export default function SubscriptionsPage() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [metrics, setMetrics] = useState<SubscriptionMetrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [metricsLoading, setMetricsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const { toast } = useToast()

  const fetchSubscriptions = async () => {
    try {
      setLoading(true)
      const response = await api.get<Subscription[]>('/subscriptions')
      setSubscriptions(response.data)
      setError(null)
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message)
      } else {
        setError('Failed to fetch subscriptions')
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchMetrics = async () => {
    try {
      setMetricsLoading(true)
      const response = await api.get<SubscriptionMetrics>('/subscriptions/metrics')
      setMetrics(response.data)
    } catch {
      // Silently fail for metrics
    } finally {
      setMetricsLoading(false)
    }
  }

  useEffect(() => {
    fetchSubscriptions()
    fetchMetrics()
  }, [])

  const handleCancelClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription)
    setCancelDialogOpen(true)
  }

  const handleCancelConfirm = async () => {
    if (!selectedSubscription) return

    setIsCancelling(true)
    try {
      await api.post(`/subscriptions/${selectedSubscription.id}/cancel`, {})
      toast({
        title: 'Subscription cancelled',
        description: `Subscription ${selectedSubscription.subscription_no} has been cancelled`,
        variant: 'success',
      })
      setCancelDialogOpen(false)
      fetchSubscriptions()
      fetchMetrics()
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof ApiRequestError ? err.message : 'Failed to cancel subscription',
        variant: 'destructive',
      })
    } finally {
      setIsCancelling(false)
    }
  }

  const columns: Column<Subscription>[] = [
    {
      key: 'subscription_no',
      header: 'Subscription No',
      sortable: true,
    },
    {
      key: 'customer_email',
      header: 'Customer',
      sortable: true,
    },
    {
      key: 'product_name',
      header: 'Product',
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (sub) => `${formatCurrency(sub.amount, sub.currency)}/${sub.billing_cycle}`,
    },
    {
      key: 'status',
      header: 'Status',
      render: (sub) => {
        const config = getStatusConfig(sub.status)
        return <Badge className={config.className}>{config.label}</Badge>
      },
    },
    {
      key: 'next_billing_date',
      header: 'Next Billing',
      render: (sub) => sub.next_billing_date ? formatDate(sub.next_billing_date) : '-',
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (sub) => (
        sub.status === 'active' && (
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              handleCancelClick(sub)
            }}
          >
            Cancel
          </Button>
        )
      ),
    },
  ]

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Subscriptions</h1>
        <p className="text-muted-foreground mt-2">
          Manage customer subscriptions and view metrics
        </p>
      </div>

      {/* Metrics Cards */}
      <div className="grid gap-4 md:grid-cols-4 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active Subscriptions</CardDescription>
          </CardHeader>
          <CardContent>
            {metricsLoading ? (
              <div className="animate-pulse h-8 bg-muted rounded w-16" />
            ) : (
              <div className="text-2xl font-semibold">
                {metrics ? formatNumber(metrics.active_count) : '-'}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Monthly Recurring Revenue</CardDescription>
          </CardHeader>
          <CardContent>
            {metricsLoading ? (
              <div className="animate-pulse h-8 bg-muted rounded w-24" />
            ) : (
              <div className="text-2xl font-semibold">
                {metrics ? formatCurrency(metrics.mrr, 'CNY') : '-'}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Churn Rate</CardDescription>
          </CardHeader>
          <CardContent>
            {metricsLoading ? (
              <div className="animate-pulse h-8 bg-muted rounded w-16" />
            ) : (
              <div className="text-2xl font-semibold">
                {metrics ? formatPercent(metrics.churn_rate) : '-'}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg Lifetime Value</CardDescription>
          </CardHeader>
          <CardContent>
            {metricsLoading ? (
              <div className="animate-pulse h-8 bg-muted rounded w-24" />
            ) : (
              <div className="text-2xl font-semibold">
                {metrics ? formatCurrency(metrics.avg_lifetime_value, 'CNY') : '-'}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {error && (
        <Card className="mb-4 border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={fetchSubscriptions}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All Subscriptions</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            data={subscriptions}
            columns={columns}
            keyField="id"
            searchPlaceholder="Search by subscription no or email..."
            searchFields={['subscription_no', 'customer_email', 'product_name']}
            isLoading={loading}
            emptyMessage="No subscriptions found."
          />
        </CardContent>
      </Card>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel subscription{' '}
              <strong>{selectedSubscription?.subscription_no}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              The subscription will be cancelled at the end of the current billing period
              ({selectedSubscription?.current_period_end ? formatDate(selectedSubscription.current_period_end) : 'N/A'}).
              The customer will retain access until then.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
              Keep Subscription
            </Button>
            <Button variant="destructive" onClick={handleCancelConfirm} disabled={isCancelling}>
              {isCancelling ? 'Cancelling...' : 'Cancel Subscription'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
